import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidV4 } from 'uuid';

import ReportTableContent from '../ReportTableContent/ReportTableContent';
import ChevronIcon from '../../assets/svgs/ChevronIcon';
import FeedbackListModal from '../FeedbackListModal/FeedbackListModal';
import formatDate from '../../utils/formatDate';
import translate from '../../libs/i18n';
import PropTypes from 'prop-types'; 
import { reportDefaultProps } from '../../utils/defaultProps';
import { reportShape } from '../../utils/propTypesShapes';
import { useWindowSize } from '../../utils/useWindowSize';
import { breakpoints } from '../../ui/variables';

import LoadingSection from '../../components/Loading/LoadingSection';

import { ReportDetailsStyle } from './ReportDetailsStyle';

const ReportDetails = ({ report, feedbacks, currentUserId, isAdmin, onFeedbacksRefresh }) => {
  const language = useSelector((state) => state.settings.language);
  const windowSize = useWindowSize();

  const [isPhone, setIsPhone] = useState(true);
  const [activateAccordion, setActivateAccordion] = useState(false);
  const [details, setDetails] = useState();
  const [showFeedbacksModal, setShowFeedbacksModal] = useState(false);

  useEffect(() => {
    if (windowSize.width >= breakpoints.ipad.width && isPhone) {
      setIsPhone(false);
    } else if (windowSize.width < breakpoints.ipad.width && !isPhone) {
      setIsPhone(true);
    }
  }, [windowSize, isPhone]);

  useEffect(() => {
    if (report && report.exam) {
      const { exam } = report;
      const {
        module,
        doctor,
        technician,
        company,
        // eslint-disable-next-line camelcase
        date_requested,
        patient,
        status,
        series_description,
        registry,
        series_instance_uid,
      } = exam;

      const currentDetails = {
        exam: { informations: [] },
        patient: { informations: [] },
      };

      if (module) {
        if (module.name && module.name.length) {
          currentDetails.exam.name = module.name;
        }
      }
      
      if (doctor) {
        if (doctor.inscription && doctor.inscription.length) {
          currentDetails.exam.informations.push({
            id: uuidV4(),
            label: translate('medicalRecord', language),
            value: doctor.inscription,
          });
        }

        if (doctor.name && doctor.name.length) {
          currentDetails.exam.informations.push({
            id: uuidV4(),
            label: translate('medicalAssistant', language),
            value: doctor.name,
          });
        }
      }

      if (technician) {
        if (technician.name && technician.name.length) {
          currentDetails.exam.informations.push({
            id: uuidV4(),
            label: translate('operatorImage', language),
            value: technician.name,
          });
        }
      }

      if (company) {
        if (company.name && company.name.length) {
          currentDetails.exam.informations.push({
            id: uuidV4(),
            label: translate('Institution', language),
            value: company.name,
          });
        }
      }

      if (status !== 'NEW') {
        currentDetails.exam.informations.push({
          id: uuidV4(),
          label: translate('status', language),
          value: translate(status.toLowerCase(), language),
        });
      }
      // Adiciona informação se há avaliações
      if (feedbacks && feedbacks.length > 0) {
        currentDetails.exam.informations.push({
          id: uuidV4(),
          label: translate('evaluations', language),
          value: `${feedbacks.length} ${translate('evaluation' + (feedbacks.length !== 1 ? 's' : ''), language)}`,
          isEvaluationInfo: true,
        });
      }


      // eslint-disable-next-line camelcase
      if (date_requested && date_requested.length) {
        currentDetails.exam.informations.push({
          id: uuidV4(),
          label: translate('examDate', language),
          value: formatDate(exam.date_requested.split('-').join(""),
                            language === 'pt-br'
                              ? 'dd/mm/yyyy'
                              : 'mm/dd/yyyy')
        });
      }

      if( series_description && series_description.length) {
        currentDetails.exam.informations.push({
          id: uuidV4(),
          label: translate('serieDescriptionWithoutAsterisk', language),
          value: series_description,
        });
      }

      if( registry && registry.length) {
        currentDetails.exam.informations.push({
          id: uuidV4(),
          label: translate('registry', language),
          value: registry,
        });
      }

      if( series_instance_uid && series_instance_uid.length) {
        currentDetails.exam.informations.push({
          id: uuidV4(),
          label: translate('series_uid', language),
          value: series_instance_uid,
        });
      }
      

      if (patient) {
        if (patient.name && patient.name.length) {
          currentDetails.patient.name = patient.name;
        }

        if (patient.sex && patient.sex.length) {
          currentDetails.patient.informations.push({
            id: uuidV4(),
            label: translate('sex', language),
            value: translate(patient.sex.toLowerCase(), language),
          });
        }

        if (patient.age && patient.age.length) {
          const age = parseInt(patient.age, 10);

          currentDetails.patient.informations.push({
            id: uuidV4(),
            label: translate('age', language),
            value: `${age} ${translate(
              `year${age === 0 || age > 1 ? 's' : ''}`,
              language
            )}`,
          });
        }

        if (patient.height) {
          currentDetails.patient.informations.push({
            id: uuidV4(),
            label: translate('bodyHeight', language),
            value: `${patient.height} cm`,
          });
        }

        if (patient.weight) {
          currentDetails.patient.informations.push({
            id: uuidV4(),
            label: translate('weight', language),
            value: `${patient.weight} Kg`,
          });
        }

        if (patient.bmi) {
          currentDetails.patient.informations.push({
            id: uuidV4(),
            label: translate('bmi', language),
            value: `${patient.bmi} Kg/m³`,
          });
        }
      }

      setDetails(currentDetails);
    }
  }, [report, feedbacks, language, setDetails]);

  const onClickToggleAccordion = () => setActivateAccordion(!activateAccordion);

  const handleCloseFeedbacksModal = () => {
    setShowFeedbacksModal(false);
  };

  return (
    <ReportDetailsStyle
      className={`${
        isPhone
          ? `details-content-accordion${activateAccordion ? ' opened' : ''}`
          : 'details-content-static'
      }`}
    >
      {details ? (
        <div>
          {details && details.exam.name && details.exam.name.length ? (
            <button
              className="toggle-accordion"
              type="button"
              onClick={onClickToggleAccordion}
            >
              {details.exam.name}
              <span className="icon">
                <ChevronIcon />
              </span>
            </button>
          ) : null}
          <div className="accordion-contents">
            {details && details.exam ? (
              <div className="exam-content">
                <ReportTableContent
                  title={!isPhone ? translate('description', language) : ''}
                  contentName={
                    !isPhone && details.exam.name && details.exam.name.length
                      ? details.exam.name
                      : ''
                  }
                  rows={details.exam.informations}
                  noBorderBottomRadius={!!isPhone}
                  onEvaluationsClick={() => setShowFeedbacksModal(true)}
                />
              </div>
            ) : null}
            {details && details.patient ? (
              <div className="patient-content">
                <ReportTableContent
                  title={translate('patient', language)}
                  contentName={details.patient.name || ''}
                  rows={details.patient.informations}
                />
              </div>
            ) : null}
          </div>
        </div>
      ) : <LoadingSection message={translate('loadingExamInfo', language)} />}

      {showFeedbacksModal && feedbacks && feedbacks.length > 0 && (
        <FeedbackListModal
          reportId={report?.id}
          feedbacks={feedbacks}
          onClose={handleCloseFeedbacksModal}
          currentUserId={currentUserId}
          isAdmin={isAdmin}
          onRefresh={onFeedbacksRefresh}
        />
      )}
    </ReportDetailsStyle>
  );
};

ReportDetails.propTypes = { 
  report: reportShape, 
  feedbacks: PropTypes.arrayOf(PropTypes.object),
  currentUserId: PropTypes.number,
  isAdmin: PropTypes.bool,
  onFeedbacksRefresh: PropTypes.func,
};

ReportDetails.defaultProps = { 
  report: reportDefaultProps, 
  feedbacks: [],
  currentUserId: null,
  isAdmin: false,
  onFeedbacksRefresh: null,
};

export default ReportDetails;
